import { supabaseClient } from "@/lib/supabase";
import type { UserT } from "@patient-watch/types/database";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";
import { diaryKeys } from "@/features/diaries/api/diaryKeys";
import { createProfileQueryKey, invalidateProfileQuery, patchProfileQueryData } from "./selectProfile";
import type { ProfileT } from "@patient-watch/types/database";
type PartialUser = Partial<UserT>;

function useUserMutation(userId: string) {
    // const auth = useAuth();
    // const userId = auth?.id as string;
    const queryClient = useQueryClient();
    const toastCopy = useToastCopy();
    const profileQueryKey = createProfileQueryKey(userId);

    return useMutation({
        mutationFn: async (data: PartialUser) => {
            return supabaseClient
                .from("users")
                .update(data)
                .eq("user_id", userId)
                .select()
                .throwOnError()
                .single()
                .then((result) => result.data);
        },
        onMutate: async (data: PartialUser) => {
            await queryClient.cancelQueries({ queryKey: profileQueryKey });
            const previousProfile = queryClient.getQueryData<ProfileT | null>(profileQueryKey);
            patchProfileQueryData(queryClient, userId, data);
            return { previousProfile };
        },
        onError: (error, _variables, context) => {
            if (context?.previousProfile !== undefined) {
                queryClient.setQueryData(profileQueryKey, context.previousProfile);
            }
            toast.error(error.message);
        },
        onSuccess: () => {
            toast.success(toastCopy.profileUpdatedSuccess);
        },
        onSettled: () => {
            void invalidateProfileQuery(queryClient, userId);
            // Also invalidate invited-users queries in case this was an invited user update
            void queryClient.invalidateQueries({ queryKey: ["invited-users"] });
            // Refetch team list when editing from team page (e.g. name change)
            void queryClient.invalidateQueries({ queryKey: ["team-members"] });
            // Admin users directory
            void queryClient.invalidateQueries({ queryKey: ["profiles"] });
            // Patient names are denormalised on diaries_view / get_diary_by_id
            void queryClient.invalidateQueries({ queryKey: diaryKeys.all });
        },
    });
}

export default useUserMutation;
